import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateInNoteRequest,
    CreateOutNoteRequest,
    CreateTransferNoteRequest,
    UpdateInNoteRequest,
    UpdateOutNoteRequest,
    UpdateTransferNoteRequest,
} from '@/lib/api/product-lot-api'
import type { QueryClient } from '@tanstack/react-query'
import { productLotApi } from '@/lib/api/product-lot-api'
import { productLotKeys } from '@/hooks/use-product-lot-query'
import { productKeys } from '@/hooks/use-product-query'
import { scentingLotKeys } from '@/hooks/use-scenting-lot-query'
import { packagingLotKeys } from '@/hooks/use-packaging-lot-query'
import { packagingLogKeys } from '@/hooks/use-packaging-log-query'
import { m } from '@/lib/paraglide/messages'

/** Làm mới list/detail phiếu lấy hàng kho lấy hương + vật tư sau khi PNK sản phẩm
 *  trừ/hoàn số dư → picker phiếu sau không chọn lại note đã hết. */
function invalidateSourceWithdrawals(
    qc: Pick<QueryClient, 'invalidateQueries'>,
) {
    qc.invalidateQueries({ queryKey: scentingLotKeys.withdrawalNotes() })
    qc.invalidateQueries({ queryKey: packagingLotKeys.withdrawalNotes() })
}

/**
 * Dọn cache sau một thao tác làm đổi tồn của lô.
 *
 * Chỉ dọn cache lô là chưa đủ: tồn sản phẩm được đọc ở hai chỗ khác nữa — cột
 * `stock_available` ở màn danh sách và thẻ tồn kho ở màn chi tiết. Cả hai nằm
 * dưới `productKeys.all` và có staleTime 30s, nên nếu không dọn thì duyệt phiếu
 * xong quay lại vẫn thấy số trước khi nhập/xuất.
 */
export function invalidateProductLotStock(
    qc: Pick<QueryClient, 'invalidateQueries'>,
) {
    qc.invalidateQueries({ queryKey: productLotKeys.lots() })
    qc.invalidateQueries({ queryKey: productKeys.all })
}

// ============================================================================
// Stock-in note mutations
// ============================================================================

export function useCreateProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateInNoteRequest) =>
            productLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateProductLotStock(qc)
            invalidateSourceWithdrawals(qc)
            // Phiếu có thể vừa sinh ra từ một dòng sổ đóng gói (#M-16). Không làm
            // mới sổ thì trong 30 giây staleTime dòng đó vẫn hiện "Chưa có phiếu"
            // kèm nút "Tạo phiếu" — bấm tiếp là đẻ phiếu thứ hai cho cùng một mẻ.
            qc.invalidateQueries({ queryKey: packagingLogKeys.all })
            toast.success(m.pll_toast_create_pnk())
        },
    })
}

// Sửa mức CK đánh dấu của lô (ưu tiên chọn lô khi đơn CK khớp).
export function useUpdateLotDiscount() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            discountPercent,
        }: {
            id: string
            discountPercent: number
        }) => productLotApi.updateLotDiscount(id, discountPercent),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: productLotKeys.lotDetail(id) })
            invalidateProductLotStock(qc)
            toast.success(m.product_lot_discount_saved())
        },
    })
}

// Sửa vị trí kho của lô.
export function useUpdateLotPosition() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, positionId }: { id: string; positionId: string }) =>
            productLotApi.updateLotPosition(id, positionId),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: productLotKeys.lotDetail(id) })
            invalidateProductLotStock(qc)
            toast.success(m.product_lot_position_saved())
        },
    })
}

export function useUpdateProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateInNoteRequest }) =>
            productLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateProductLotStock(qc)
            invalidateSourceWithdrawals(qc)
            toast.success(m.pll_toast_update_pnk())
        },
    })
}

export function useApproveProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateProductLotStock(qc)
            toast.success(m.pll_toast_approve_pnk())
        },
    })
}

export function useRejectProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            productLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateSourceWithdrawals(qc)
            toast.success(m.pll_toast_reject_pnk())
        },
    })
}

export function useCancelProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateSourceWithdrawals(qc)
            toast.success(m.pll_toast_cancel_pnk())
        },
    })
}

/** Rollback PNK đã duyệt: BE sinh PXK điều chỉnh trừ lại tồn (M-17). */
export function useRollbackProductLotInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.rollbackInNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({ queryKey: productLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            invalidateProductLotStock(qc)
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

// ============================================================================
// Stock-out note mutations
// ============================================================================

/** Rollback PXK đã duyệt: BE sinh PNK điều chỉnh cộng lại tồn (M-17). */
export function useRollbackProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.rollbackOutNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({ queryKey: productLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            invalidateProductLotStock(qc)
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

export function useCreateProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateOutNoteRequest) =>
            productLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            toast.success(m.pll_toast_create_pxk())
        },
    })
}

export function useUpdateProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateOutNoteRequest }) =>
            productLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: productLotKeys.outNoteDetail(id) })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            invalidateProductLotStock(qc)
            toast.success(m.pll_toast_update_pxk())
        },
    })
}

export function useApproveProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            invalidateProductLotStock(qc)
            toast.success(m.pll_toast_approve_pxk())
        },
    })
}

export function useRejectProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            productLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            toast.success(m.pll_toast_reject_pxk())
        },
    })
}

export function useCancelProductLotOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.outNotes() })
            toast.success(m.pll_toast_cancel_pxk())
        },
    })
}

// ============================================================================
// Transfer note mutations (PDC-SP)
// ============================================================================

export function useCreateProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateTransferNoteRequest) =>
            productLotApi.createTransferNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            toast.success(m.pll_toast_create_pdc())
        },
    })
}

export function useUpdateProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            req,
        }: {
            id: string
            req: UpdateTransferNoteRequest
        }) => productLotApi.updateTransferNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.transferNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            toast.success(m.pll_toast_update_pdc())
        },
    })
}

export function useApproveProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.approveTransferNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.transferNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            invalidateProductLotStock(qc)
            toast.success(m.pll_toast_approve_pdc())
        },
    })
}

export function useRejectProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            productLotApi.rejectTransferNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.transferNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            toast.success(m.pll_toast_reject_pdc())
        },
    })
}

export function useCancelProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.cancelTransferNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: productLotKeys.transferNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            toast.success(m.pll_toast_cancel_pdc())
        },
    })
}

export function useDeleteProductLotTransferNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productLotApi.deleteTransferNote(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: productLotKeys.transferNotes() })
            toast.success(m.pll_toast_delete_pdc())
        },
    })
}
